import { Button, FormControlLabel, Switch, TextField } from '@mui/material';

import type {
  AutomationRow,
  AutomationsViewModel,
} from '../../view-model/Automations.viewModel';
import * as styles from '../Automations.view.css';

type Props = {
  row: AutomationRow;
  setConfigDraft: AutomationsViewModel['setConfigDraft'];
  saveConfig: AutomationsViewModel['saveConfig'];
  setIncludePushes: AutomationsViewModel['setIncludePushes'];
};

/** Product and workout pickers need GraphQL types that are not generated in
 *  this build, so ids are typed by hand; the server validates ownership. */
export const AutomationConfigView = ({
  row,
  setConfigDraft,
  saveConfig,
  setIncludePushes,
}: Props) => {
  const { automationType, config, pending } = row;
  if (config.fields.length === 0) return null;
  const hasTextFields = config.fields.some(
    (field) => field !== 'includePushes',
  );

  return (
    <form
      className={styles.configForm}
      aria-label={`${row.label} settings`}
      onSubmit={(event) => {
        event.preventDefault();
        void saveConfig(automationType);
      }}
    >
      <div className={styles.filters}>
        {config.fields.includes('firstWorkout') ? (
          <TextField
            size="small"
            label="First workout id"
            className={styles.sendTestSelect}
            value={config.draft.firstWorkoutId}
            error={Boolean(config.validation.firstWorkoutId)}
            helperText={
              config.validation.firstWorkoutId ??
              'A published workout of this creator; the deep link is minted on save'
            }
            onChange={(event) =>
              setConfigDraft(automationType, {
                firstWorkoutId: event.target.value,
              })
            }
          />
        ) : null}
        {config.fields.includes('otpPitchTargetProductId') ? (
          <TextField
            size="small"
            label="Pitch target product id"
            className={styles.sendTestSelect}
            value={config.draft.otpPitchTargetProductId}
            error={Boolean(config.validation.otpPitchTargetProductId)}
            helperText={
              config.validation.otpPitchTargetProductId ??
              'Published subscription product to pitch; leave empty when the creator has one'
            }
            onChange={(event) =>
              setConfigDraft(automationType, {
                otpPitchTargetProductId: event.target.value,
              })
            }
          />
        ) : null}
        {hasTextFields ? (
          <Button
            type="submit"
            size="small"
            variant="outlined"
            disabled={!config.canSave}
          >
            Save settings
          </Button>
        ) : null}
      </div>
      {config.fields.includes('includePushes') ? (
        <FormControlLabel
          label="Include push notifications"
          control={
            <Switch
              size="small"
              checked={config.includePushes}
              disabled={pending}
              onChange={(_event, checked) =>
                void setIncludePushes(automationType, checked)
              }
            />
          }
        />
      ) : null}
    </form>
  );
};
